import clsx from 'clsx';
import moment from 'moment';
import React, { useState, useRef, useEffect } from 'react';
import { Form } from 'react-bootstrap';
import DatePicker, { DatePickerProps } from 'react-datepicker';

import 'react-datepicker/dist/react-datepicker.css';
import './style.scss';

const MODE = ['date', 'time', 'date-time'] as const;

type Mode = typeof MODE[number];

const DEFAULT_DATE_FORMAT: Record<Mode, string> = {
	date: 'dd/MM/yyyy',
	time: 'HH:mm',
	'date-time': 'HH:mm dd/MM/yyyy',
};

const DEFAULT_OUTPUT_FORMAT: Record<Mode, string> = {
	date: 'YYYY-MM-DDTHH:mm:ss',
	time: 'HH:mm',
	'date-time': 'YYYY-MM-DDTHH:mm:ss',
};

type IProps = {
	label?: string;
	name: string;
	required?: boolean;
	placeholderText?: string;
	dateFormat?: string;
	dateFormatOutput?: string;
	labelClassName?: string;
	inputClassName?: string;
	className?: string;
	setDateValue?: (date: string | null) => void;
	handleBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
	onClickOutside?: (event: React.MouseEvent<HTMLDivElement>) => void;
	errors?: any;
	touched?: any;
	value: string | null;
	minDate?: Date;
	maxDate?: Date;
	mode?: Mode;
	disabled?: boolean;
	[x: string]: any;
};

const days = ['CN', 'Th2', 'Th3', 'Th4', 'Th5', 'Th6', 'Th7']; 

const months = [
	'Tháng 1',
	'Tháng 2',
	'Tháng 3',
	'Tháng 4',
	'Tháng 5',
	'Tháng 6',
	'Tháng 7',
	'Tháng 8',
	'Tháng 9',
	'Tháng 10',
	'Tháng 11',
	'Tháng 12',
];

const datepickerLocale = {
	localize: {
		day: (n: number) => days[n],
		month: (n: number) => months[n]
	},
	formatLong: {
        date: () => "dd/MM/yyyy",
        time: () => "HH:mm",
        dateTime: () => "dd/MM/yyyy HH:mm"
    }
};

// Component cho time picker với 2 cột riêng biệt
const TimePickerColumns = ({
	date,
	onTimeChange,
	disabled
}: {
	date: moment.Moment;
	onTimeChange: (hour: number, minute: number) => void;
	disabled?: boolean;
}) => {
	const [selectedHour, setSelectedHour] = useState(date.isValid() ? date.hour() : 0);
	const [selectedMinute, setSelectedMinute] = useState(date.isValid() ? date.minute() : 0);
	const hourRef = useRef<HTMLDivElement>(null);
	const minuteRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		if (date.isValid()) {
			setSelectedHour(date.hour());
			setSelectedMinute(date.minute());
		}
	}, [date]);

	useEffect(() => {
		// Scroll to selected hour
		if (hourRef.current) {
			const selectedElement = hourRef.current.querySelector('.selected');
			if (selectedElement) {
				selectedElement.scrollIntoView({ block: 'center' });
			}
		}
	}, [selectedHour]);

	useEffect(() => {
		// Scroll to selected minute
		if (minuteRef.current) {
			const selectedElement = minuteRef.current.querySelector('.selected');
			if (selectedElement) {
				selectedElement.scrollIntoView({ block: 'center' });
			}
		}
	}, [selectedMinute]);

	const handleHourClick = (hour: number) => {
		setSelectedHour(hour);
		onTimeChange(hour, selectedMinute);
	};

	const handleMinuteClick = (minute: number) => {
		setSelectedMinute(minute);
		onTimeChange(selectedHour, minute);
	};

	const hours = Array.from({ length: 24 }, (_, i) => i);
	const minutes = Array.from({ length: 60 }, (_, i) => i);

	return (
		<div className="oct-time-picker-columns">
			<div className="oct-time-header-wrapper">
				<div className="time-column-header">Giờ</div>
				<div className="time-column-header">Phút</div>
			</div>
			<div className="oct-time-lists-wrapper">
				<div className="time-column-list" ref={hourRef}>
					{hours.map((hour) => (
						<div
							key={hour}
							className={clsx('time-item', { selected: hour === selectedHour })}
							onClick={() => !disabled && handleHourClick(hour)}
						>
							{hour.toString().padStart(2, '0')}
						</div>
					))}
				</div>
				<div className="time-column-list" ref={minuteRef}>
					{minutes.map((minute) => (
						<div
							key={minute}
							className={clsx('time-item', { selected: minute === selectedMinute })}
							onClick={() => !disabled && handleMinuteClick(minute)}
						>
							{minute.toString().padStart(2, '0')}
						</div>
					))}
				</div>
			</div>
		</div>
	);
};

const DatePickerCustom = ({
	placeholderText = '__/__/____',
	dateFormat = 'dd/MM/yyyy',
	dateFormatOutput,
	label,
	required,
	labelClassName,
	inputClassName,
	className,
	name,
	value,
	setDateValue,
	touched,
	errors,
	handleBlur,
	onClickOutside,
	minDate,
	maxDate,
	mode = 'date',
	disabled,
	...props
}: IProps) => {
	const inputFormat = DEFAULT_DATE_FORMAT[mode];
	const outputFormat = dateFormatOutput || DEFAULT_OUTPUT_FORMAT[mode];
	const date = moment(value, [outputFormat, inputFormat]);
	const hasError = touched && !!errors;

	const inputRef = useRef<HTMLInputElement>(null);
	const hasInteractedWithTimePicker = useRef(false);
	const hasInteractedWithCalendar = useRef(false);
	const hasOpenedCalendar = useRef(false);
	const hasCalledBlur = useRef(false);

	const handleCalendarOpen = () => {
		hasOpenedCalendar.current = true;
		hasCalledBlur.current = false; // Reset flag khi mở calendar
	};

	const handleChange = (date: Date | null, event: React.SyntheticEvent<any> | undefined) => {
		hasInteractedWithCalendar.current = true;

		// Nếu user nhập tay vào input
		if (event?.type === 'change' && event?.currentTarget) {
			const inputValue = (event.currentTarget as HTMLInputElement).value;
			if (inputValue) {
				const tempFormat = mode === 'date' ? 'DDMMYYYY' : ['HH:mm DDMMYYYY', 'HHmm DDMMYYYY'];
				const parsedDate = moment(inputValue, tempFormat);
				if (parsedDate.isValid()) {
					setDateValue?.(parsedDate.format(outputFormat));
				}
				return;
			} else {
				// Input rỗng
				setDateValue?.(null);
				return;
			}
		}

		// Nếu chọn từ calendar hoặc phím
		setDateValue?.(moment(date).isValid() ? moment(date).format(outputFormat) : null);
	};

	const handleTimeChange = (hour: number, minute: number) => {
		hasInteractedWithTimePicker.current = true;
		const currentDate = date.isValid() ? date.clone() : moment();
		currentDate.hour(hour);
		currentDate.minute(minute);
		setDateValue?.(currentDate.format(outputFormat));
	};

	const onBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		// Nếu đã gọi handleBlur rồi thì không gọi lại
		if (hasCalledBlur.current) {
			return;
		}

		const inputValue = event.target.value;

		if (inputValue === '') {
			setDateValue?.(null);
			hasCalledBlur.current = true;
			handleBlur?.(event);
			return;
		}

		const tempFormat = mode === 'date' ? 'DDMMYYYY' : ['HH:mm DDMMYYYY', 'HHmm DDMMYYYY'];
		const valueFomat = moment(inputValue, tempFormat);
		const isValidDate = valueFomat.isValid();

		if (isValidDate) {
			setDateValue?.(valueFomat.format(outputFormat));
		}

		hasCalledBlur.current = true;
		handleBlur?.(event);
	};

	const handleCalendarClose = () => {
		// Nếu có mở calendar hoặc tương tác với calendar/time picker, trigger callbacks
		if (hasOpenedCalendar.current || hasInteractedWithCalendar.current || hasInteractedWithTimePicker.current) {
			// Chỉ gọi handleBlur nếu chưa được gọi
			if (!hasCalledBlur.current && handleBlur && inputRef.current) {
				const syntheticEvent = {
					target: inputRef.current,
					currentTarget: inputRef.current,
				} as React.FocusEvent<HTMLInputElement>;
				hasCalledBlur.current = true;
				handleBlur(syntheticEvent);
			}
			if (onClickOutside) {
				onClickOutside({} as React.MouseEvent<HTMLDivElement>);
			}
			hasOpenedCalendar.current = false;
			hasInteractedWithTimePicker.current = false;
			hasInteractedWithCalendar.current = false;
		}
	};

	return (
		<Form.Group className={clsx('align-items-center w-100 oct-field-wrapper', className)}>
			{label && (
				<Form.Label
					className={clsx(
						'spaces text-lable-input max-content-width mb-0 min-w-fit-content',
						labelClassName
					)}
				>
					{required ? (
						<span>
							{label}
							<span className="text-danger"> (*)</span>
						</span>
					) : (
						label
					)}
				</Form.Label>
			)}
			<div className="position-relative w-100">
				<DatePicker
					className={clsx('form-control customs-input w-100', inputClassName)}
					showIcon={true}
					icon="bi bi-calendar4-week"
					toggleCalendarOnIconClick
					minDate={minDate}
					maxDate={maxDate}
					wrapperClassName={clsx('oct-date-picker-wrapper w-100', {
						'is-invalid': !!hasError,
						'oct-has-custom-time': mode === 'date-time' || mode === 'time'
					})}
					popperClassName={clsx('oct-date-picker-popper', {
						'oct-popper-has-time': mode === 'date-time' || mode === 'time'
					})}
					calendarClassName={clsx({
						'oct-with-custom-time': mode === 'date-time',
						'oct-time-only': mode === 'time'
					})}
					selected={date.isValid() ? date.toDate() : null}
					dateFormat={inputFormat}
					placeholderText={placeholderText}
					name={name}
					onChange={handleChange}
					onBlur={onBlur}
					onCalendarOpen={handleCalendarOpen}
					disabled={disabled}
					showPopperArrow={false}
					showTimeSelectOnly={mode === 'time'}
					shouldCloseOnSelect={mode === 'date'} // Chỉ đóng khi chọn ngày (không có time)
					portalId="root-portal"
					locale={datepickerLocale}
					calendarStartDay={1}
					onCalendarClose={handleCalendarClose}
					ref={inputRef as any}
					{...props}
				>
					{(mode === 'date-time' || mode === 'time') && (
						<TimePickerColumns
							date={date}
							onTimeChange={handleTimeChange}
							disabled={disabled}
						/>
					)}
				</DatePicker>
				<Form.Control.Feedback type="invalid" tooltip className="field-tooltip-error">
					{errors}
				</Form.Control.Feedback>
			</div>
		</Form.Group>
	);
};

export default DatePickerCustom;
